import React from 'react';
import { useRouter } from '../context/RouterContext';

const Terms: React.FC = () => {
  const { navigate } = useRouter();

  const sectionClass = "text-gray-700 dark:text-slate-300";
  const headingClass = "text-gray-900 dark:text-white";

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-3 py-6">
        <button
          onClick={() => navigate('home')}
          className="flex items-center text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors group"
        >
          <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>

        <div className="bg-white dark:bg-slate-800/50 rounded-2xl shadow-soft dark:shadow-soft-dark p-6 border border-gray-200/50 dark:border-slate-700/50">
          <h1 className={`text-3xl font-bold ${headingClass} mb-4`}>Terms of Service</h1>
          
          <div className="prose prose-gray dark:prose-invert max-w-none">
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              <strong>Last updated:</strong> October 16, 2025
            </p>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>1. Acceptance of Terms</h2>
              <p className={`${sectionClass} mb-4`}>
                By accessing and using Rate My Plate ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. 
                If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>2. Description of Service</h2>
              <p className={`${sectionClass} mb-4`}>
                Rate My Plate is a community-driven platform that allows users to share, discover, and rate custom license plates. 
                The service includes uploading and sharing license plate photographs, voting on and rating license plates, 
                browsing and discovering license plates from other users, and participating in community discussions and reactions.
              </p>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>3. User Accounts and Responsibilities</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>While you may browse without creating an account, certain features require user identification through our browser fingerprinting system. You are responsible for maintaining the accuracy of any information you provide and ensuring your use complies with these terms.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>4. Content Guidelines and Restrictions</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p><strong>Acceptable Content:</strong> You may only upload license plate images that you have photographed yourself or have permission to share, that are appropriate for all audiences, and that do not contain personal information or identifying details.</p>
                <p><strong>Prohibited Content:</strong> You may not upload content that is illegal, harmful, threatening, abusive, defamatory, violates privacy rights, contains malware, is spam, infringes intellectual property, or contains hate speech.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>5. Intellectual Property Rights</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>By uploading content, you grant us a non-exclusive, royalty-free, worldwide license to use, display, and distribute your content. You retain ownership. The Rate My Plate service, including its design and technology, is protected by intellectual property laws.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>6. Privacy and Data Protection</h2>
              <p className={`${sectionClass} mb-4`}>
                Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.
              </p>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>7. User Conduct and Prohibited Activities</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>You agree not to circumvent our voting system, use automated tools, harass other users, upload prohibited content, gain unauthorized access, or interfere with the service.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>8. Service Availability and Modifications</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>We strive for continuous availability but do not guarantee it. We may modify, suspend, or discontinue the service at any time. We reserve the right to update these Terms; continued use constitutes acceptance.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>9. Disclaimers and Limitations of Liability</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>The service is provided "as is" without warranties. To the maximum extent permitted by law, Rate My Plate shall not be liable for any indirect, incidental, special, consequential, or punitive damages.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>10. Termination</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>We may terminate or suspend your access immediately for any reason, including breach of these Terms. You may stop using the service at any time.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>11. Governing Law and Dispute Resolution</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>These Terms shall be governed by the laws of the United States. Disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>12. Contact Information</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>If you have any questions about these Terms of Service, please contact us:</p>
                <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-xl">
                  <p><strong>Email:</strong> r8mypl8@gmail.com</p>
                  <p><strong>Website:</strong> <a href="/" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">r8mypl8.net</a></p>
                </div>
              </div>
            </section>

            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-700">
              <p className="text-sm text-gray-500 dark:text-slate-500">
                By using Rate My Plate, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
              <p className="text-sm text-gray-500 dark:text-slate-500 mt-2">© 2025 Rate My Plate. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
